import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Home as HomeIcon,
  Building2,
  KeyRound,
  MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "../../utils/footer";
import TrustSection from "../common/TrustSection";
import ContactCTA from "../common/ContactCTA";
import PageSeo from "../common/PageSeo";

// ======== Property Data ========
const properties = [
  {
    id: 1,
    title: "Marathon Nexzone",
    location:
      "Marathon Nexzone, National Highway 4B – JNPT Highway, Near Palaspe Phata, Panvel, Mumbai, Maharashtra – 410206",
    price: "₹85 L - ₹1.5 Cr",
    img: "/uploads/marathon.jpg",
  },
  {
    id: 2,
    title: "Hiranandani Fortune City",
    location:
      "Survey No. 30, Bhokharpada Village, Taluka - Panvel, District - Raigad, NH-4, Maharashtra, 410206",
    price: "₹75 L - ₹2.2 Cr",
    img: "/uploads/hiranandani.jpg",
  },
  {
    id: 3,
    title: "Godrej City Homes",
    location:
      "Village Thombrewadi, Post Taluka, Poyanje, Panvel, Maharashtra 410206",
    price: "₹55 L - ₹1.2 Cr",
    img: "/uploads/godrej.jpeg",
  },
  {
    id: 4,
    title: "Wadhwa Wise City",
    location:
      "Wadhwa Wise City, Opp. Old Mumbai Pune Highway, Village Bhingarwadi, Panvel, Mumbai, Maharashtra 410206",
    price: "₹95 L - ₹1.8 Cr",
    img: "/uploads/wadhava.jpg",
  },
  {
    id: 5,
    title: "Indiabulls Green Towers",
    location:
      "Opp. Honda Warehouse, Rasayani Road, Near Kon Village, Panvel, Navi Mumbai, Maharashtra, 410206",
    price: "₹1.2 Cr - ₹2.5 Cr",
    img: "/uploads/indiabulls.jpg",
  },
];

// ======== Testimonials ========
const testimonials = [
  {
    name: "Rajesh Patil",
    feedback:
      "Thakkar Realtors helped me find my dream home in Panvel. Their transparency and professionalism were top-notch!",
  },
  {
    name: "Neha Sharma",
    feedback:
      "Very smooth experience! Mr. Chirag guided me through every step and helped me make the right investment.",
  },
  {
    name: "Suresh Iyer",
    feedback:
      "Honest advice, great communication, and reliable service. Highly recommend Thakkar Realtors for any property need.",
  },
];

// ======== Animation Variants ========
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay },
  }),
};

const Home = () => {
  const featuredRef = useRef(null);
  const scrollToFeatured = () => {
    featuredRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll-based parallax effect
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.7]);

  return (
    <div className="overflow-x-hidden">
      <PageSeo
        title="Home"
        description="Explore verified properties with Thakkhar Realtors in Panvel and Navi Mumbai with transparent assistance and trusted advisory."
      />

      {/* ================= HERO SECTION ================= */}
      <section className="relative flex items-center justify-center h-[90vh] overflow-hidden">
        {/* Animated Background */}
        <motion.div
          className="absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: "url('/uploads/panvel.jpg')",
            y,
            scale,
            opacity,
          }}
        ></motion.div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/70"></div>

        {/* Text */}
        <motion.div
          className="relative z-10 text-center text-white px-6"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
            Find Your{" "}
            <span className="text-blue-400">Dream Home</span> with <br />{" "}
            Thakkhar Realtors
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto text-gray-100 mb-8">
            Discover luxury properties and exclusive deals across Navi Mumbai
            and Panvel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={scrollToFeatured}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Properties <ArrowRight className="w-5 h-5" />
            </motion.button>
            <Link to="/contact">
              <motion.button
                className="border border-white text-white px-8 py-3 rounded-xl font-medium hover:bg-white hover:text-blue-600 transition shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ================= ABOUT SECTION ================= */}
      <motion.section
        className="max-w-6xl mx-auto text-center py-20 px-6"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          About <span className="text-blue-600">Thakkhar Realtors</span>
        </h2>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Thakkhar Realtors is a professional real estate consultancy offering
          trusted property solutions across Navi Mumbai and Panvel. We
          specialize in buying, selling, and leasing premium residential and
          commercial spaces — with a commitment to integrity, transparency, and
          client satisfaction.
        </p>
      </motion.section>

      {/* ================= SERVICES SECTION ================= */}
      <motion.section
        className="bg-gray-50 py-20 px-6"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Our <span className="text-blue-600">Services</span>
          </h2>
          <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
            Explore our core real estate services designed to make your property
            journey simple, transparent, and insightful.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            {[HomeIcon, Building2, KeyRound, MapPin].map((Icon, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-lg transition"
                whileHover={{ scale: 1.03 }}
                variants={fadeUp}
                custom={i * 0.2}
              >
                <Icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-blue-700 mb-2">
                  {["Buy Property", "Sell Property", "Rental Services", "Investment Advice"][i]}
                </h3>
                <p className="text-gray-500 text-sm">
                  {
                    [
                      "Find verified RERA-approved residential and commercial projects.",
                      "Get the best value for your property with professional marketing and support.",
                      "Affordable and verified rental listings for tenants and landlords.",
                      "Expert guidance on high growth location opportunities and market planning.",
                    ][i]
                  }
                </p>
              </motion.div>
            ))}
          </div>

          <Link to="/services">
            <motion.button
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2 mx-auto shadow-md"
              whileHover={{ scale: 1.05 }}
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </div>
      </motion.section>

      {/* ================= WHY CHOOSE US ================= */}
      <motion.section
        className="bg-gray-50 py-20 px-6"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            Why Choose <span className="text-blue-600">Thakkar Realtors</span>?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              "Verified Properties",
              "Transparent Deals",
              "Expert Guidance",
              "Local Expertise",
            ].map((title, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-lg transition"
                whileHover={{ scale: 1.05 }}
              >
                <CheckCircle className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-blue-700 mb-2">
                  {title}
                </h3>
                <p className="text-gray-500 text-sm">
                  {
                    [
                      "All listings are verified and RERA-approved.",
                      "We believe in honesty and zero hidden charges.",
                      "Get support from experienced property advisors.",
                      "Deep knowledge of Navi Mumbai & Panvel markets.",
                    ][i]
                  }
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <TrustSection title="Trust Indicators" />
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <motion.section
        className="bg-gray-50 py-20 px-6"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            What Our <span className="text-blue-600">Clients Say</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl shadow-md border hover:shadow-xl transition"
                whileHover={{ scale: 1.03 }}
              >
                <p className="text-gray-600 italic mb-4">“{t.feedback}”</p>
                <h4 className="text-blue-700 font-semibold">— {t.name}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ================= FEATURED PROPERTIES ================= */}
      <motion.section
        ref={featuredRef}
        className="max-w-7xl mx-auto py-36 px-6 text-center"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          Featured <span className="text-blue-600">Properties</span>
        </h2>
        <p className="text-gray-500 max-w-2xl mx-auto mb-12">
          Explore our most popular and sought-after properties handpicked for
          your lifestyle and investment needs.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {properties.map((property) => (
            <Link key={property.id} to="/properties">
              <motion.div
                className="bg-white rounded-xl shadow-md border overflow-hidden hover:shadow-xl transition cursor-pointer flex flex-col h-full"
                whileHover={{ scale: 1.03 }}
              >
                <div className="w-full h-56 overflow-hidden">
                  <motion.img
                    src={property.img}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-500"
                    whileHover={{ scale: 1.08 }}
                  />
                </div>
                <div className="p-5 text-left flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-600">
                      {property.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1 line-clamp-3">
                      {property.location}
                    </p>
                    <p className="text-gray-800 font-medium mt-3">
                      {property.price}
                    </p>
                  </div>
                  <motion.button
                    className="mt-5 bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 transition w-full"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    View Details
                  </motion.button>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.section>

      {/* ================= CALL TO ACTION ================= */}
      <motion.section
        className="bg-blue-600 py-16 text-center text-white px-6"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <h2 className="text-3xl font-bold mb-4">
          Ready to Find Your Perfect Home?
        </h2>
        <p className="max-w-2xl mx-auto mb-8">
          Contact Thakkhar Realtors today and let our experts guide you to the
          best property deals in Navi Mumbai and Panvel.
        </p>
        <Link to="/contact">
          <button className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition">
            Get in Touch
          </button>
        </Link>
      </motion.section>

      <section className="max-w-7xl mx-auto pt-[38px] px-6 pb-16">
        <ContactCTA />
      </section>

  

      {/* ================= FOOTER ================= */}
      <Footer />
    </div>
  );
};

export default Home;
